/**
 * Copyright 2022 (C) Blink - All Rights Reserved
 * 
 * This source code is protected under international copyright law.  All rights
 * reserved and protected by the copyright holders.
 * All files are confidential and only available to authorized individuals with the
 * permission of the copyright holders. If you encounter any file and do not have
 * permission, please get in touch with the copyright holders and delete this file.
 */
import checkFormFields from "utils/checkFormFields";
import warning from "utils/warning";

interface ITryCatchOptions {
  onError?: (error: Error | unknown) => void;
  onFinally?: (isError: boolean) => any;
  setLoading?: (isLoading: boolean) => void;
  fieldsToValidate?: { [key in string]: any } | null;
  isAsync?: boolean;
}

interface IUseTryCatch {
  (fn: (...args: any[]) => Promise<any>, options?: ITryCatchOptions): (
    ...args: any[]
  ) => Promise<boolean | void>;
  (
    fn: (...args: any[]) => Promise<any>,
    setLoading: (isLoading: boolean) => void
  ): (...args: any[]) => Promise<boolean | void>;
}

const useTryCatch: IUseTryCatch = (fn, options) => {
  const settings: ITryCatchOptions = {
    setLoading: undefined,
    onFinally: undefined,
    onError: undefined,
    fieldsToValidate: null,
    isAsync: true,
  };
  if (options && typeof options === "object") {
    Object.assign(settings, options);
  }
  if (typeof options === "function") {
    settings.setLoading = options;
  }
  const { onError, onFinally, setLoading, fieldsToValidate, isAsync } =
    settings;
  // basically the idea is to wrap the function declaration with `useTryCatch` which will return an augmented function with a try-catch block
  if (isAsync) {
    return async (...args: any[]) => {
      let isError = false,
        value;
      try {
        if (setLoading) setLoading(true);
        if (fieldsToValidate) {
          const isValid = checkFormFields(fieldsToValidate);
          if (!isValid) return;
        }
        value = await fn(...args);
      } catch (error) {
        warning(error);
        if (onError) onError(error);
        isError = true;
      } finally {
        if (setLoading) setLoading(false);
        if (onFinally) {
          const payload = onFinally(isError);
          if (payload) {
            value = payload;
          }
        }
      }
      return value;
    };
  } else {
    return (...args: any[]) => {
      let isError = false,
        value;
      try {
        if (setLoading) setLoading(true);
        if (fieldsToValidate) {
          const isValid = checkFormFields(fieldsToValidate);
          if (!isValid) return;
        }
        value = fn(...args);
      } catch (error) {
        warning(error);
        if (onError) onError(error);
        isError = true;
      } finally {
        if (setLoading) setLoading(false);
        if (onFinally) {
          const payload = onFinally(isError);
          if (payload) {
            value = payload;
          }
        }
      }
      return value;
    };
  }
};

export const tryCatcher = (
  fn: (...args: any[]) => any,
  options: ITryCatchOptions
) => {
  const settings: ITryCatchOptions = {
    onError: undefined,
    onFinally: undefined,
    isAsync: false,
  };
  if (options && typeof options === "object") {
    Object.assign(settings, options);
  }
  const { onError, onFinally, isAsync } = settings;
  if (isAsync) {
    return async (...args: any[]) => {
      let isError = false,
        value;
      try {
        value = await fn(...args);
      } catch (error) {
        warning(error);
        if (onError) onError(error);
        isError = true;
      } finally {
        if (onFinally) {
          const payload = onFinally(isError);
          if (payload) {
            value = payload;
          }
        }
      }
      return value;
    };
  } else {
    return (...args: any[]) => {
      let isError = false,
        value;
      try {
        value = fn(...args);
      } catch (error) {
        warning(error);
        if (onError) onError(error);
        isError = true;
      } finally {
        if (onFinally) {
          const payload = onFinally(isError);
          if (payload) {
            value = payload;
          }
        }
      }
      return value;
    };
  }
};

export default useTryCatch;
